import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { AlertTriangle, ArrowRight } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What must every formula start with?',
    options: [
      'A cell reference like A1',
      'The = (equals) sign',
      'The function name like SUM',
      'A number',
    ],
    correctIndex: 1,
    explanation:
      'Every formula in Google Sheets must begin with the = sign. Without it, Sheets treats your input as plain text.',
  },
  {
    question: 'What does =SUM(B2:B5) return for this data?',
    context: {
      headers: ['Offer ID', 'Clicks'],
      rows: [
        ['offer_123', '340'],
        ['offer_456', '210'],
        ['offer_123', '420'],
        ['offer_456', '180'],
      ],
    },
    options: [
      '4 (the count of cells)',
      '420 (the largest value)',
      '1,150 (the total)',
      '287.5 (the average)',
    ],
    correctIndex: 2,
    explanation:
      'SUM adds up all the numeric values in the given range. 340 + 210 + 420 + 180 = 1,150.',
  },
  {
    question:
      'An offer had 800 clicks and 24 conversions. What is the conversion rate?',
    options: ['0.3%', '30%', '3%', '33.3%'],
    correctIndex: 2,
    explanation:
      'Conversion Rate = Conversions / Clicks = 24 / 800 = 0.03 = 3%.',
  },
  {
    question: 'What does =IFERROR(A1/B1, 0) do?',
    options: [
      'Always returns 0',
      'Divides A1 by B1, but shows 0 if there is an error',
      'Checks if A1 equals B1',
      'Returns an error message',
    ],
    correctIndex: 1,
    explanation:
      'IFERROR wraps a formula and catches any error. If A1/B1 works, it shows the result. If it errors (e.g., divide by zero), it shows 0 instead.',
  },
  {
    question: 'What is the difference between COUNT and SUM?',
    options: [
      'They do the same thing',
      'COUNT adds values, SUM counts cells',
      'COUNT counts how many cells have values, SUM adds the values together',
      'SUM works on text, COUNT works on numbers',
    ],
    correctIndex: 2,
    explanation:
      'COUNT tells you how many cells contain numeric values. SUM tells you the total of those values. COUNT(10, 20, 30) = 3, SUM(10, 20, 30) = 60.',
  },
  {
    question: 'What does the * operator do in a formula?',
    options: [
      'Adds two values together',
      'Divides one value by another',
      'Multiplies two values together',
      'Raises a value to a power',
    ],
    correctIndex: 2,
    explanation:
      'The * (asterisk) operator multiplies values. For example, =A1*B1 multiplies the value in A1 by the value in B1.',
  },
  {
    question: 'If you type SUM(A1:A5) without the = sign, what happens?',
    options: [
      'Sheets calculates the sum anyway',
      'Sheets displays the text "SUM(A1:A5)" as plain text',
      'Sheets shows an error',
      'Sheets asks you if you meant to write a formula',
    ],
    correctIndex: 1,
    explanation:
      'Without the = sign, Sheets treats everything as plain text. It will literally display "SUM(A1:A5)" in the cell instead of calculating the sum.',
  },
  {
    question: '=AVERAGE(10, 20, 30) returns what?',
    options: ['60', '30', '20', '3'],
    correctIndex: 2,
    explanation:
      'AVERAGE calculates the mean of the given values. (10 + 20 + 30) / 3 = 60 / 3 = 20.',
  },
  {
    question: 'What error appears when you divide a number by zero in Sheets?',
    options: [
      '#VALUE!',
      '#REF!',
      '#DIV/0!',
      '#NULL!',
    ],
    correctIndex: 2,
    explanation:
      'Dividing by zero produces a #DIV/0! error. This is why you should wrap division formulas in IFERROR to handle cases where the denominator might be zero.',
  },
  {
    question:
      'Looking at this data, what formula in D2 calculates the conversion rate for offer_123?',
    context: {
      headers: ['Offer ID', 'Clicks', 'Conversions', 'Conv. Rate'],
      rows: [
        ['offer_123', '2,000', '50', '?'],
        ['offer_456', '800', '24', '3.0%'],
      ],
    },
    options: [
      '=B2/C2',
      '=C2/B2',
      '=C2*B2',
      '=B2-C2',
    ],
    correctIndex: 1,
    explanation:
      'Conversion Rate = Conversions / Clicks. The formula is =C2/B2 (50/2000), which gives 0.025 or 2.5%.',
  },
  {
    question: 'What does =COUNT(B2:B6) return for this data?',
    context: {
      headers: ['Day', 'Clicks'],
      rows: [
        ['Mon', '10'],
        ['Tue', '20'],
        ['Wed', '(blank)'],
        ['Thu', '30'],
        ['Fri', '40'],
      ],
    },
    options: ['5', '4', '100', '0'],
    correctIndex: 1,
    explanation:
      'COUNT only counts cells that contain numeric values and ignores blanks. There are 4 numbers in the range (10, 20, 30, 40), so COUNT returns 4.',
  },
  {
    question: 'What does =IFERROR(100/0, "N/A") display?',
    options: [
      '#DIV/0!',
      '0',
      'N/A',
      '100',
    ],
    correctIndex: 2,
    explanation:
      '100/0 causes a #DIV/0! error. IFERROR catches the error and returns the fallback value "N/A" instead.',
  },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Formula Toggle Demo ─── */
function FormulaToggle() {
  const [isFormula, setIsFormula] = useState(false)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsFormula(false)}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            !isFormula
              ? 'bg-red-100 text-red-700 border border-red-300'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          Without =
        </button>
        <button
          onClick={() => setIsFormula(true)}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            isFormula
              ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          With =
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
        {/* Formula bar */}
        <div className="flex items-center border-b border-gray-200 bg-gray-50 px-3 py-1.5 gap-2">
          <span className="text-xs font-semibold text-gray-500">C2</span>
          <div className="w-px h-4 bg-gray-300" />
          <AnimatePresence mode="wait">
            <motion.span
              key={isFormula ? 'formula' : 'text'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-sm font-mono text-gray-800"
            >
              {isFormula ? '=A2+B2' : 'A2+B2'}
            </motion.span>
          </AnimatePresence>
        </div>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 w-10" />
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 min-w-[80px]">A</th>
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 min-w-[80px]">B</th>
              <th className="border-b border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 min-w-[80px]">C</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 font-medium py-1.5">1</td>
              <td className="border-b border-r border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">Price</td>
              <td className="border-b border-r border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">Tax</td>
              <td className="border-b border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">Total</td>
            </tr>
            <tr>
              <td className="bg-gray-100 border-r border-gray-300 text-center text-xs text-gray-500 font-medium py-1.5">2</td>
              <td className="border-r border-gray-200 px-3 py-1.5 text-gray-800">100</td>
              <td className="border-r border-gray-200 px-3 py-1.5 text-gray-800">15</td>
              <AnimatePresence mode="wait">
                <motion.td
                  key={isFormula ? 'calc' : 'text'}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className={`px-3 py-1.5 font-mono ${
                    isFormula
                      ? 'text-emerald-700 bg-emerald-50 font-semibold'
                      : 'text-red-600 bg-red-50'
                  }`}
                >
                  {isFormula ? '115' : 'A2+B2'}
                </motion.td>
              </AnimatePresence>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-sm text-gray-500">
        {isFormula
          ? 'With the = sign, Sheets calculates the result: 100 + 15 = 115'
          : 'Without the = sign, Sheets displays "A2+B2" as literal text'}
      </p>
    </div>
  )
}

/* ─── SUM / COUNT / AVERAGE Demo ─── */
function AggregationDemo() {
  const [activeFunc, setActiveFunc] = useState('SUM')

  const numbers = [120, 85, '', 200, 45, '', 310, 90]
  const filled = numbers.filter((n) => n !== '')
  const sum = filled.reduce((a, b) => a + b, 0)
  const count = filled.length
  const average = Math.round(sum / count)

  const funcInfo = {
    SUM: {
      formula: '=SUM(B2:B9)',
      result: sum.toLocaleString(),
      color: 'blue',
      description: 'Adds up all numeric values in the range',
      detail: `${filled.join(' + ')} = ${sum}`,
    },
    COUNT: {
      formula: '=COUNT(B2:B9)',
      result: count.toString(),
      color: 'green',
      description: 'Counts how many cells contain numbers (ignores blanks)',
      detail: `${filled.length} cells have numbers, 2 are empty`,
    },
    AVERAGE: {
      formula: '=AVERAGE(B2:B9)',
      result: average.toString(),
      color: 'purple',
      description: 'Calculates the mean of all numeric values',
      detail: `${sum} / ${count} = ${average}`,
    },
  }

  const active = funcInfo[activeFunc]

  const highlightColorMap = {
    blue: 'bg-blue-100 border-blue-400',
    green: 'bg-green-100 border-green-400',
    purple: 'bg-purple-100 border-purple-400',
  }

  return (
    <div className="space-y-4">
      {/* Function tabs */}
      <div className="flex gap-2">
        {Object.keys(funcInfo).map((fn) => {
          const info = funcInfo[fn]
          const colorClasses = {
            blue: 'bg-blue-100 text-blue-700 border-blue-300',
            green: 'bg-green-100 text-green-700 border-green-300',
            purple: 'bg-purple-100 text-purple-700 border-purple-300',
          }
          const inactiveClass = 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          return (
            <button
              key={fn}
              onClick={() => setActiveFunc(fn)}
              className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                activeFunc === fn ? colorClasses[info.color] : inactiveClass
              }`}
            >
              {fn}
            </button>
          )
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Mini grid */}
        <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
          <div className="flex items-center border-b border-gray-200 bg-gray-50 px-3 py-1.5 gap-2">
            <span className="text-xs font-semibold text-gray-500">B10</span>
            <div className="w-px h-4 bg-gray-300" />
            <span className="text-sm font-mono text-gray-800">{active.formula}</span>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border-b border-r border-gray-300 px-2 py-1 text-center text-xs text-gray-500 w-8" />
                <th className="border-b border-r border-gray-300 px-2 py-1 text-center text-xs text-gray-500 w-20">A</th>
                <th className="border-b border-gray-300 px-2 py-1 text-center text-xs text-gray-500 w-20">B</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1">1</td>
                <td className="border-b border-r border-gray-200 px-2 py-1 font-semibold text-gray-700 bg-gray-50">Day</td>
                <td className="border-b border-gray-200 px-2 py-1 font-semibold text-gray-700 bg-gray-50">Clicks</td>
              </tr>
              {numbers.map((n, i) => (
                <tr key={i}>
                  <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1">
                    {i + 2}
                  </td>
                  <td className="border-b border-r border-gray-200 px-2 py-1 text-gray-600 text-xs">
                    Day {i + 1}
                  </td>
                  <td
                    className={`border-b border-gray-200 px-2 py-1 text-center transition-colors duration-200 ${
                      n !== ''
                        ? highlightColorMap[active.color]
                        : 'bg-gray-50 text-gray-300 italic'
                    }`}
                  >
                    {n !== '' ? n : '(empty)'}
                  </td>
                </tr>
              ))}
              {/* Result row */}
              <tr>
                <td className="bg-gray-100 border-r border-gray-300 text-center text-xs text-gray-500 py-1">
                  10
                </td>
                <td className="border-r border-gray-200 px-2 py-1 font-semibold text-gray-700 bg-gray-50">
                  {activeFunc}
                </td>
                <AnimatePresence mode="wait">
                  <motion.td
                    key={activeFunc}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className={`px-2 py-1 text-center font-bold text-lg ${highlightColorMap[active.color]}`}
                  >
                    {active.result}
                  </motion.td>
                </AnimatePresence>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Explanation */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFunc}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col justify-center space-y-3"
          >
            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-sm text-emerald-400 font-mono">{active.formula}</code>
            </div>
            <p className="text-gray-700 font-medium">{active.description}</p>
            <p className="text-sm text-gray-500">{active.detail}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ─── Conversion Rate Interactive ─── */
function ConversionRateDemo() {
  const [clicks, setClicks] = useState(1250)
  const [conversions, setConversions] = useState(45)

  const hasError = clicks === 0
  const rawRate = hasError ? null : conversions / clicks
  const percentage = hasError ? null : (rawRate * 100).toFixed(1)
  const iferrorResult = hasError ? '0' : rawRate.toFixed(4)

  const presets = [
    { label: 'Normal', clicks: 1250, conversions: 45 },
    { label: 'High CR', clicks: 500, conversions: 75 },
    { label: 'Zero clicks', clicks: 0, conversions: 10 },
    { label: 'No conversions', clicks: 800, conversions: 0 },
  ]

  return (
    <div className="space-y-4">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => {
              setClicks(p.clicks)
              setConversions(p.conversions)
            }}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              clicks === p.clicks && conversions === p.conversions
                ? 'border-blue-300 bg-blue-100 text-blue-700'
                : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Mini spreadsheet */}
      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
        <div className="flex items-center border-b border-gray-200 bg-gray-50 px-3 py-1.5 gap-2">
          <span className="text-xs font-semibold text-gray-500">D2</span>
          <div className="w-px h-4 bg-gray-300" />
          <span className="text-sm font-mono text-gray-800">=IFERROR(C2/B2, 0)</span>
        </div>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 w-10" />
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500">A</th>
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500">B</th>
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500">C</th>
              <th className="border-b border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500">D</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1.5">1</td>
              <td className="border-b border-r border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">Offer</td>
              <td className="border-b border-r border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">Clicks</td>
              <td className="border-b border-r border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">Conv.</td>
              <td className="border-b border-gray-200 px-3 py-1.5 font-semibold text-gray-700 bg-gray-50">CR%</td>
            </tr>
            <tr>
              <td className="bg-gray-100 border-r border-gray-300 text-center text-xs text-gray-500 py-1.5">2</td>
              <td className="border-r border-gray-200 px-3 py-1.5 text-gray-800">offer_123</td>
              <td className="border-r border-gray-200 px-3 py-1.5 text-blue-700 bg-blue-50 font-medium">
                {clicks.toLocaleString()}
              </td>
              <td className="border-r border-gray-200 px-3 py-1.5 text-green-700 bg-green-50 font-medium">
                {conversions}
              </td>
              <AnimatePresence mode="wait">
                <motion.td
                  key={`${clicks}-${conversions}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={`px-3 py-1.5 font-mono font-bold text-center ${
                    hasError
                      ? 'bg-yellow-50 text-yellow-700'
                      : 'bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {hasError ? '0' : `${percentage}%`}
                </motion.td>
              </AnimatePresence>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Breakdown */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${clicks}-${conversions}`}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          {hasError ? (
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Division by zero!</p>
                <p className="text-sm text-yellow-700 mt-1">
                  <code className="bg-yellow-100 px-1 rounded">={conversions}/0</code> would cause a <code className="bg-yellow-100 px-1 rounded">#DIV/0!</code> error.
                  IFERROR catches it and shows <strong>0</strong> instead.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <code className="bg-gray-100 px-2 py-1 rounded font-mono">
                {conversions} / {clicks.toLocaleString()} = {iferrorResult}
              </code>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <span className="font-semibold text-emerald-700">{percentage}%</span>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module4() {
  return (
    <ModuleLayout moduleId={4}>
      <div className="space-y-8">
        {/* Section 1: Formulas Start with = */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">
                Formulas Start with <code className="text-emerald-600">=</code>
              </h2>
              <p className="text-lg text-gray-600">
                Every formula in Google Sheets starts with the equals sign{' '}
                <code className="rounded bg-gray-100 px-2 py-0.5 text-emerald-700 font-semibold">=</code>.
                Without it, Sheets treats your input as plain text.
              </p>
            </div>

            <FormulaToggle />

            {/* Operators reference */}
            <div>
              <h3 className="mb-3 text-lg font-semibold text-gray-800">Basic Operators</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { op: '+', name: 'Add', example: '=A1+B1', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                  { op: '-', name: 'Subtract', example: '=A1-B1', color: 'bg-green-50 border-green-200 text-green-800' },
                  { op: '*', name: 'Multiply', example: '=A1*B1', color: 'bg-purple-50 border-purple-200 text-purple-800' },
                  { op: '/', name: 'Divide', example: '=A1/B1', color: 'bg-amber-50 border-amber-200 text-amber-800' },
                ].map((item) => (
                  <motion.div
                    key={item.op}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className={`rounded-lg border p-3 text-center ${item.color}`}
                  >
                    <div className="text-2xl font-bold">{item.op}</div>
                    <div className="text-sm font-medium">{item.name}</div>
                    <code className="mt-1 block text-xs opacity-70">{item.example}</code>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Section 2: SUM, COUNT, AVERAGE */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">SUM, COUNT, AVERAGE</h2>
              <p className="text-lg text-gray-600">
                Three essential functions for aggregating data. Click each tab to see how they work with the same data.
              </p>
            </div>

            <AggregationDemo />

            {/* Quick reference */}
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                {
                  fn: 'SUM',
                  desc: 'Add up a range of numbers',
                  syntax: '=SUM(B2:B10)',
                  color: 'border-blue-200 bg-blue-50',
                  iconColor: 'text-blue-600',
                },
                {
                  fn: 'COUNT',
                  desc: 'Count cells that contain numbers',
                  syntax: '=COUNT(B2:B10)',
                  color: 'border-green-200 bg-green-50',
                  iconColor: 'text-green-600',
                },
                {
                  fn: 'AVERAGE',
                  desc: 'Calculate the mean of a range',
                  syntax: '=AVERAGE(B2:B10)',
                  color: 'border-purple-200 bg-purple-50',
                  iconColor: 'text-purple-600',
                },
              ].map((item) => (
                <div key={item.fn} className={`rounded-lg border p-4 ${item.color}`}>
                  <h4 className="font-bold text-gray-800">{item.fn}</h4>
                  <p className="text-sm text-gray-600 mt-1">{item.desc}</p>
                  <code className="mt-2 block text-xs font-mono text-gray-700 bg-white/60 rounded px-2 py-1">
                    {item.syntax}
                  </code>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 3: Conversion Rate */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Conversion Rate</h2>
              <p className="text-lg text-gray-600">
                This is <strong>the formula you will use most</strong>:{' '}
                <code className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-800 font-semibold">
                  Conversion Rate = Conversions / Clicks
                </code>
              </p>
            </div>

            <ConversionRateDemo />

            {/* IFERROR callout */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-amber-200 bg-amber-50 p-6"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-amber-800">Always use IFERROR for division</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    <code className="bg-amber-100 rounded px-1.5 py-0.5">=IFERROR(C2/B2, 0)</code>
                    {' '} means: divide C2 by B2, but if there is any error (like dividing by zero), show 0 instead.
                    This prevents ugly <code className="bg-amber-100 rounded px-1">#DIV/0!</code> errors in your reports.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Section 4: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={4} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
